/**
 * useDocuments - Main hook for document state management.
 *
 * Combines:
 * - API fetching on mount
 * - SSE event handling via useDocumentEvents
 * - Upload handling
 * - Automatic refresh after completion
 *
 * Usage:
 * const { feedData, loading, error, upload, refreshDocuments } = useDocuments();
 */

import { useReducer, useEffect, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { documentsReducer, createInitialState } from './reducer';
import {
  selectFeedData,
  selectDocsNeedingStepRecovery,
  selectHasProcessingDocs,
  DocumentData,
} from './selectors';
import { DocumentsState, PipelineStep, ApiDocument } from './types';
import { apiDocumentFromResponse } from './apiDocumentFromResponse';
import { useDocumentEvents } from '../useDocumentEvents';
import { documentService, DuplicateUploadError, JobStatus, SortBy, ViewMode, CategoryCount } from '../../services/documentService';

/** Default page size for pagination */
export const DEFAULT_PAGE_SIZE = 14;

export interface UseDocumentsOptions {
  /** Whether to enable SSE connection (default: true) */
  enableSSE?: boolean;
  /** Whether to auto-fetch on mount (default: true) */
  autoFetch?: boolean;
  /** Refresh interval in ms when SSE is disconnected (default: 10000) */
  pollInterval?: number;
  /** Initial sort field (default: 'document_date') */
  initialSortBy?: SortBy;
  /** Initial view mode (default: 'all') */
  initialViewMode?: ViewMode;
  /** Page size (default: 20) */
  pageSize?: number;
  /** classifier-v3 (#1066): native taxonomy-v2 domain lens filter */
  domain?: string | null;
  /** classifier-v3 (#1066): native taxonomy-v2 role subcategory filter */
  role?: string | null;
  /** Search query (optional) */
  search?: string | null;
  /** Show only deleted documents (default: false) */
  showDeleted?: boolean;
}

export interface BatchUploadResult {
  successful: { docId: string; jobId: string }[];
  failed: { filename: string; error: string }[];
  /**
   * Server-accepted duplicates: HTTP 200 with ``duplicate: true``.
   * The upload was re-routed to the existing doc and the pipeline
   * may reprocess. Counted to surface the "wird neu verarbeitet"
   * toast without listing each filename.
   */
  duplicates: number;
  /**
   * Server-rejected duplicates: HTTP 409 from the content-hash
   * dedup branch in ``backend/.../documents/_crud.py``. These are
   * NOT re-processable — the existing doc is whole and the upload
   * was rejected. The UI shows a distinct "already in your account"
   * toast with the filename(s) instead of a generic upload error.
   */
  duplicatesRejected: { filename: string; existingDocId?: string }[];
  /**
   * #1600 — restored documents: HTTP 200 with ``restored: true``. The upload
   * matched a soft-deleted doc and the backend cleared its ``deleted_at`` (the
   * original re-surfaces in the feed) instead of creating a duplicate or
   * rejecting. Counted to surface a distinct "wiederhergestellt" toast — never
   * a silent success or the misleading "wird neu verarbeitet" reprocess toast.
   */
  restored: number;
}

export interface UseDocumentsReturn {
  /** Transformed data ready for DocumentFeed */
  feedData: DocumentData[];
  /** Loading state for initial fetch */
  loading: boolean;
  /** Error from last API call */
  error: string | null;
  /** SSE connection status */
  sseConnected: boolean;
  /** Raw state (for debugging) */
  state: DocumentsState;
  /** Upload a new document */
  upload: (file: File, comment?: string) => Promise<{ docId: string; jobId: string }>;
  /** Upload multiple documents */
  uploadBatch: (files: File[], comment?: string) => Promise<BatchUploadResult>;
  /** Manually refresh documents from API */
  refreshDocuments: () => Promise<void>;
  /** Optimistically update tag IDs for a document (before API call) */
  updateDocumentTagsOptimistic: (docId: string, tagIds: string[]) => void;

  // Pagination
  /** Current page (0-indexed) */
  currentPage: number;
  /** Total number of documents */
  total: number;
  /** Page size */
  pageSize: number;
  /** Go to specific page */
  setPage: (page: number) => void;

  // Sorting & View Mode
  /** Current sort field */
  sortBy: SortBy;
  /** Change sort field */
  setSortBy: (sort: SortBy) => void;
  /** Current view mode */
  viewMode: ViewMode;
  /** Change view mode */
  setViewMode: (mode: ViewMode) => void;
  /** Number of new documents since last viewed */
  newDocsCount: number;
  /** Mark documents as viewed (resets new docs counter) */
  markAsViewed: () => Promise<void>;

  // Lens counts (from backend, across all documents)
  /**
   * classifier-v3 (#1066): native (domain, role) lens counts —
   * ``{domain: {count, name, subcategories: {role: {count, name}}}}``. The sole
   * lens-count source since the classifier-v1 decommission (#2009) removed the
   * legacy ``category_counts``.
   */
  domainCounts: Record<string, CategoryCount>;
  /** Total documents before filtering (for "Alle Dokumente" count in sidebar) */
  totalAll: number;
  /**
   * #1889 — total non-``filtered`` documents. The "Alle Dokumente" badge/tile
   * wires to this (not ``totalAll``) so the count equals the feed, which hides
   * ``filtered`` docs (logos, unparseable finance sheets).
   */
  totalAllVisible: number;
}

/**
 * Delay for refetching after job completion.
 * Gives backend time to update document metadata.
 */
const COMPLETION_REFETCH_DELAY = 500;

/**
 * Main documents hook.
 */
export function useDocuments(options: UseDocumentsOptions = {}): UseDocumentsReturn {
  const {
    enableSSE = true,
    autoFetch = true,
    pollInterval = 10000,
    initialSortBy = 'document_date',
    initialViewMode = 'all',
    pageSize: configuredPageSize = DEFAULT_PAGE_SIZE,
    domain = null,
    role = null,
    search = null,
    showDeleted = false,
  } = options;

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(documentsReducer, undefined, createInitialState);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [totalAllVisible, setTotalAllVisible] = useState(0);
  const [newDocsCount, setNewDocsCount] = useState(0);
  const [sortBy, setSortByState] = useState<SortBy>(initialSortBy);
  const [viewMode, setViewModeState] = useState<ViewMode>(initialViewMode);
  const [domainCounts, setDomainCounts] = useState<Record<string, CategoryCount>>({});
  const pageSize = configuredPageSize;

  // Refs for cleanup and avoiding stale closures
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const completionRefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recoveryAttemptedRef = useRef<boolean>(false);
  const stateRef = useRef(state);
  // Request ID to prevent race conditions - only process the latest request's response
  const latestRequestIdRef = useRef<number>(0);
  // Debounce timer for search input
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // Track if initial fetch has happened
  const initialFetchDoneRef = useRef<boolean>(false);

  // ==========================================
  // API Fetching
  // ==========================================

  const fetchDocuments = useCallback(async (
    page: number = currentPage,
    sort: SortBy = sortBy,
    view: ViewMode = viewMode
  ) => {
    // Increment request ID to track this request
    const requestId = ++latestRequestIdRef.current;

    dispatch({ type: 'FETCH_START' });

    try {
      const offset = page * pageSize;
      const response = await documentService.listDocuments({
        offset,
        limit: pageSize,
        sort_by: sort,
        view,
        // Only include filters if they are set
        ...(domain && { domain }),
        ...(role && { role }),
        ...(search && { search }),
        ...(showDeleted && { deleted_only: true }),
      });

      // Check if this response is stale (a newer request was made while waiting)
      if (requestId !== latestRequestIdRef.current) {
        console.log('[useDocuments] Ignoring stale response', { requestId, latest: latestRequestIdRef.current });
        return;
      }

      const timestamp = Date.now();

      console.log('[useDocuments] API response:', {
        requestId,
        offset: response.offset,
        limit: response.limit,
        total: response.total,
        new_docs_count: response.new_docs_count,
        documentsCount: response.documents.length,
        domainCounts: response.domain_counts,
      });

      // Update pagination state from response
      setTotal(response.total);
      setTotalAll(response.total_all || response.total);
      // #1889 — the "Alle Dokumente" badge/tile uses the non-filtered count so
      // it equals the feed. Fall back to total_all → total for a backend that
      // predates the key (never leaves the badge at 0 when docs exist).
      setTotalAllVisible(response.total_all_visible ?? response.total_all ?? response.total);
      setNewDocsCount(response.new_docs_count);
      setDomainCounts(response.domain_counts || {});

      // Convert to ApiDocument format (the API response matches). Projection
      // extracted to `apiDocumentFromResponse` so it (incl. the #1965
      // is_multi_document marker) is unit-testable without a hook harness.
      const documents: ApiDocument[] = response.documents.map(apiDocumentFromResponse);

      dispatch({ type: 'FETCH_SUCCESS', documents, timestamp });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load documents';
      dispatch({ type: 'FETCH_ERROR', error: message });
    }
  }, [currentPage, sortBy, viewMode, pageSize, domain, role, search, showDeleted]);

  // ==========================================
  // Job Status Recovery
  // ==========================================

  /**
   * Recover step info for documents that are processing but have no currentStep.
   * This happens after page refresh when SSE hasn't caught up yet.
   * Uses stateRef to always read current state without triggering re-renders.
   */
  const recoverJobSteps = useCallback(async () => {
    const currentState = stateRef.current;
    const allDocs = Array.from(currentState.documents.values());
    const processingDocs = allDocs.filter(
      (doc) => doc.status === 'processing' || doc.status === 'pending'
    );
    const docsNeedingRecovery = selectDocsNeedingStepRecovery(currentState);

    console.log('[useDocuments] Recovery check:', {
      totalDocs: allDocs.length,
      processingDocs: processingDocs.length,
      needingRecovery: docsNeedingRecovery.length,
      processingDocsDetails: processingDocs.map((d) => ({
        docId: d.docId,
        jobId: d.jobId,
        status: d.status,
        currentStep: d.currentStep,
      })),
    });

    if (docsNeedingRecovery.length === 0) return;

    // Split docs into those with job_id and those without
    const docsWithJobId = docsNeedingRecovery.filter((doc) => doc.jobId != null);
    const docsWithoutJobId = docsNeedingRecovery.filter((doc) => doc.jobId == null);

    const timestamp = Date.now();

    // Recover docs with job_id using batch job status
    if (docsWithJobId.length > 0) {
      const jobIds = docsWithJobId.map((doc) => doc.jobId as string);
      console.log('[useDocuments] Recovering by job IDs:', jobIds);

      try {
        const statuses = await documentService.getBatchJobStatus(jobIds);
        console.log('[useDocuments] Got job statuses (by job_id):', statuses);

        for (const status of statuses) {
          dispatch({
            type: 'POLL_JOB_STATUS',
            docId: status.doc_id,
            status: status as JobStatus,
            timestamp,
          });
        }
      } catch (err) {
        console.error('[useDocuments] Failed to recover job steps by job_id:', err);
      }
    }

    // Recover docs without job_id using doc_id lookup
    if (docsWithoutJobId.length > 0) {
      const docIds = docsWithoutJobId.map((doc) => doc.docId);
      console.log('[useDocuments] Recovering by doc IDs (no job_id available):', docIds);

      try {
        const statuses = await documentService.getJobsByDocIds(docIds);
        console.log('[useDocuments] Got job statuses (by doc_id):', statuses);

        let needsRefresh = false;
        for (const status of statuses) {
          // Filter out any invalid statuses (TypeScript knows status can only be the defined types)
          if (status.status === 'pending' || status.status === 'processing' ||
              status.status === 'completed' || status.status === 'failed') {
            dispatch({
              type: 'POLL_JOB_STATUS',
              docId: status.doc_id,
              status: status as JobStatus,
              timestamp,
            });
          } else {
            // Document has status 'processing' but no job found - likely completed
            // Need to refresh document list to get actual status
            console.log(`[useDocuments] Doc ${status.doc_id} has no job - scheduling refresh`);
            needsRefresh = true;
          }
        }
        // If any docs had no job, refresh to get actual status from backend
        if (needsRefresh) {
          setTimeout(() => fetchDocuments(), 200);
        }
      } catch (err) {
        console.error('[useDocuments] Failed to recover job steps by doc_id:', err);
      }
    }
  }, [fetchDocuments]);

  // ==========================================
  // SSE Event Handlers
  // ==========================================

  const handleSSEConnectionChange = useCallback((connected: boolean) => {
    if (connected) {
      dispatch({ type: 'SSE_CONNECTED' });
    } else {
      dispatch({ type: 'SSE_DISCONNECTED' });
    }
  }, []);

  const handleStepQueued = useCallback((docId: string, step: string) => {
    dispatch({
      type: 'SSE_STEP_QUEUED',
      docId,
      step: step as PipelineStep,
      timestamp: Date.now(),
    });
  }, []);

  const handleStepStarted = useCallback((docId: string, step: string, retryAttempt?: number, processingStatus?: string) => {
    dispatch({
      type: 'SSE_STEP_STARTED',
      docId,
      step: step as PipelineStep,
      retryAttempt,
      processingStatus,
      timestamp: Date.now(),
    });
  }, []);

  const handleCompleted = useCallback(
    (docId: string) => {
      dispatch({
        type: 'SSE_COMPLETED',
        docId,
        timestamp: Date.now(),
      });

      // Invalidate metadata cache so preview shows fresh extracted facts
      queryClient.invalidateQueries({ queryKey: ['document-metadata', docId] });

      // Schedule refetch to get full metadata
      if (completionRefetchTimeoutRef.current) {
        clearTimeout(completionRefetchTimeoutRef.current);
      }
      completionRefetchTimeoutRef.current = setTimeout(() => {
        fetchDocuments();
      }, COMPLETION_REFETCH_DELAY);
    },
    [fetchDocuments, queryClient]
  );

  const handleFailed = useCallback((docId: string, error: string) => {
    dispatch({
      type: 'SSE_FAILED',
      docId,
      error,
      timestamp: Date.now(),
    });
  }, []);

  const handleDocumentCreated = useCallback(
    (docId: string, filename: string, parentDocId?: string) => {
      dispatch({
        type: 'SSE_DOCUMENT_CREATED',
        docId,
        filename,
        parentDocId,
        timestamp: Date.now(),
      });
    },
    []
  );

  // Connect SSE
  useDocumentEvents({
    enabled: enableSSE,
    onConnectionChange: handleSSEConnectionChange,
    onStepQueued: handleStepQueued,
    onStepStarted: handleStepStarted,
    onCompleted: handleCompleted,
    onFailed: handleFailed,
    onDocumentCreated: handleDocumentCreated,
  });

  // ==========================================
  // Upload Handler
  // ==========================================

  const upload = useCallback(
    async (file: File, comment: string = ''): Promise<{ docId: string; jobId: string }> => {
      const response = await documentService.upload(file, comment);
      const timestamp = Date.now();

      // Only dispatch UPLOAD_STARTED when a job_id is present.
      // Duplicate-already-processing responses return {doc_id, duplicate: true}
      // without a job_id — dispatching with undefined would corrupt jobId state
      // and cause the recovery filter to send "undefined" to the job status API.
      if (response.job_id) {
        dispatch({
          type: 'UPLOAD_STARTED',
          docId: response.doc_id,
          jobId: response.job_id,
          filename: file.name,
          timestamp,
        });
      }

      return {
        docId: response.doc_id,
        jobId: response.job_id,
      };
    },
    []
  );

  // ==========================================
  // Batch Upload Handler
  // ==========================================

  const uploadBatch = useCallback(
    async (files: File[], comment: string = ''): Promise<BatchUploadResult> => {
      const successful: { docId: string; jobId: string }[] = [];
      const failed: { filename: string; error: string }[] = [];
      const duplicatesRejected: { filename: string; existingDocId?: string }[] = [];
      let duplicates = 0;
      let restored = 0;

      for (const file of files) {
        try {
          const response = await documentService.upload(file, comment);
          const timestamp = Date.now();

          // #1600 — the upload matched a soft-deleted doc and the backend
          // restored it (no new row, no job, content unchanged). Count it so
          // the page shows a distinct "wiederhergestellt" toast, and track the
          // doc as successful, but skip the duplicate/reprocess accounting.
          if (response.restored) {
            restored++;
            successful.push({ docId: response.doc_id, jobId: response.job_id });
            continue;
          }

          if (response.duplicate) {
            duplicates++;
          }

          if (response.job_id) {
            dispatch({
              type: 'UPLOAD_STARTED',
              docId: response.doc_id,
              jobId: response.job_id,
              filename: file.name,
              timestamp,
            });
          }

          successful.push({
            docId: response.doc_id,
            jobId: response.job_id,
          });
        } catch (err) {
          // Content-hash dedup hits (HTTP 409) are NOT a generic upload
          // failure — surface them separately so the page can show
          // "already in your account" instead of "upload failed".
          if (err instanceof DuplicateUploadError) {
            duplicatesRejected.push({
              filename: err.filename,
              existingDocId: err.existingDocId,
            });
            continue;
          }
          const errorMessage = err instanceof Error ? err.message : 'Upload failed';
          failed.push({ filename: file.name, error: errorMessage });
          console.error(`[useDocuments] Failed to upload ${file.name}:`, err);
        }
      }

      return { successful, failed, duplicates, duplicatesRejected, restored };
    },
    []
  );

  // ==========================================
  // Pagination Controls
  // ==========================================

  const setPage = useCallback((page: number) => {
    setCurrentPage(page);
    fetchDocuments(page, sortBy, viewMode);
  }, [fetchDocuments, sortBy, viewMode]);

  const setSortBy = useCallback((sort: SortBy) => {
    setSortByState(sort);
    setCurrentPage(0); // Reset to first page when sort changes
    fetchDocuments(0, sort, viewMode);
  }, [fetchDocuments, viewMode]);

  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
    setCurrentPage(0); // Reset to first page when view mode changes
    fetchDocuments(0, sortBy, mode);
  }, [fetchDocuments, sortBy]);

  const markAsViewed = useCallback(async () => {
    try {
      // IMPORTANT: Fetch documents BEFORE marking as viewed
      // Otherwise, markDocumentsViewed() updates last_viewed_upload_order to max,
      // and then view='new' filter returns empty (no docs with upload_order > max)
      setViewModeState('new');
      setCurrentPage(0);
      await fetchDocuments(0, sortBy, 'new');

      // Now mark as viewed (so next time the counter resets)
      await documentService.markDocumentsViewed();
      // Reset counter locally
      setNewDocsCount(0);
    } catch (err) {
      console.error('[useDocuments] Failed to mark documents as viewed:', err);
    }
  }, [fetchDocuments, sortBy]);

  // ==========================================
  // Effects
  // ==========================================

  // Initial fetch (runs once on mount, and on category/subcategory changes)
  useEffect(() => {
    if (autoFetch) {
      // Reset to page 0 when filters change (except for initial mount)
      if (initialFetchDoneRef.current) {
        setCurrentPage(0);
      }
      fetchDocuments(0, sortBy, viewMode);
      initialFetchDoneRef.current = true;
    }
    // Don't include search here - it's handled by the debounced effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch, domain, role, showDeleted]);

  // Debounced search effect - triggers 300ms after user stops typing
  useEffect(() => {
    // Skip on initial render (handled by initial fetch effect)
    if (!initialFetchDoneRef.current) return;

    // Clear any pending debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Debounce search requests
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(0);
      fetchDocuments(0, sortBy, viewMode);
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Recover job steps once after initial fetch completes
  useEffect(() => {
    if (!state.loading && state.lastFetchedAt !== null && !recoveryAttemptedRef.current) {
      recoveryAttemptedRef.current = true;
      // Small delay to ensure state ref is updated
      const timeoutId = setTimeout(() => {
        recoverJobSteps();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [state.loading, state.lastFetchedAt, recoverJobSteps]);

  // Fallback polling when SSE is disconnected
  const hasProcessingDocs = selectHasProcessingDocs(state);
  useEffect(() => {
    // Only poll as fallback when SSE is disconnected and we have processing docs
    if (!state.sseConnected && hasProcessingDocs) {
      // Don't start if already polling
      if (pollTimeoutRef.current) return;

      pollTimeoutRef.current = setInterval(() => {
        fetchDocuments();
      }, pollInterval);
    } else {
      // Clear polling when SSE reconnects or no processing docs
      if (pollTimeoutRef.current) {
        clearInterval(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    }

    return () => {
      if (pollTimeoutRef.current) {
        clearInterval(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [state.sseConnected, hasProcessingDocs, fetchDocuments, pollInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearInterval(pollTimeoutRef.current);
      }
      if (completionRefetchTimeoutRef.current) {
        clearTimeout(completionRefetchTimeoutRef.current);
      }
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // ==========================================
  // Derived Data
  // ==========================================

  const feedData = selectFeedData(state, t);

  return {
    feedData,
    loading: state.loading,
    error: state.error,
    sseConnected: state.sseConnected,
    state,
    upload,
    uploadBatch,
    refreshDocuments: () => fetchDocuments(currentPage, sortBy, viewMode),
    updateDocumentTagsOptimistic: (docId: string, tagIds: string[]) => {
      dispatch({ type: 'OPTIMISTIC_TAG_UPDATE', docId, tagIds });
    },

    // Pagination
    currentPage,
    total,
    pageSize,
    setPage,

    // Sorting & View Mode
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    newDocsCount,
    markAsViewed,

    // Lens counts
    domainCounts,
    totalAll,
    totalAllVisible,
  };
}
